// src/utils/helpConditionalBlocks.js
// Processes :::if / :::endif conditional blocks in help markdown content.
// Blocks are shown or hidden based on the user's features, billing mode,
// permissions, and org settings.

/**
 * Evaluate a single condition string against user context.
 *
 * Supported formats:
 *   feature:core_ehr,erx_mobile   — OR: any listed feature enabled
 *   billing:cash,hybrid           — OR: billing mode matches any
 *   permission:admin_manage_users  — AND: all listed permissions required
 *   setting:uses_cases            — OR: any listed org setting is truthy
 *
 * Prefix with "not " to negate: "not feature:core_ehr"
 *
 * @param {string} conditionLine - The condition string (everything after ":::if ")
 * @param {{ solutions: object, billingMode: string, can: function, orgSettings: object }} ctx
 * @returns {boolean}
 */
export function evaluateCondition(conditionLine, ctx) {
  if (!conditionLine || typeof conditionLine !== 'string') return true;

  const trimmed = conditionLine.trim();
  if (!trimmed) return true;

  // Check for negation
  let negated = false;
  let expression = trimmed;
  if (expression.startsWith('not ')) {
    negated = true;
    expression = expression.slice(4).trim();
  }

  // Parse type:values
  const colonIdx = expression.indexOf(':');
  if (colonIdx === -1) return true; // malformed — show content

  const type = expression.slice(0, colonIdx).trim();
  const valuesStr = expression.slice(colonIdx + 1).trim();
  if (!valuesStr) return true; // malformed — show content

  const values = valuesStr.split(',').map(v => v.trim()).filter(Boolean);
  if (values.length === 0) return true;

  let result;

  switch (type) {
    case 'feature':
      // OR — any listed feature is enabled
      result = values.some(f => ctx.solutions?.[f]);
      break;

    case 'billing':
      // OR — current billing mode matches any listed value
      result = values.includes(ctx.billingMode);
      break;

    case 'permission':
      // AND — all listed permissions are required
      result = values.every(p => ctx.can?.(p));
      break;

    case 'setting':
      // OR — any listed org setting is truthy
      result = values.some(s => ctx.orgSettings?.[s]);
      break;

    default:
      // Unknown type — show content (safe fallback)
      return true;
  }

  return negated ? !result : result;
}

/**
 * Process all :::if / :::endif conditional blocks in markdown content.
 * Blocks whose conditions evaluate to false are removed.
 * Unknown or malformed conditions default to showing the content.
 *
 * @param {string} content - Raw markdown with :::if / :::endif blocks
 * @param {{ solutions: object, billingMode: string, can: function, orgSettings: object }} ctx
 * @returns {string} Processed markdown
 */
export function processConditionalBlocks(content, ctx) {
  if (!content || typeof content !== 'string') return content || '';

  // Match :::if <condition>\n...\n:::endif where the body does NOT contain
  // another :::if directive. The negative lookahead (?!^:::if\s) ensures only
  // truly innermost blocks are matched on each pass.
  // Flags: g = global, m = multiline (^ matches line start)
  const pattern = /^:::if\s+(.+)\r?\n((?:(?!^:::if\s)[\s\S])*?)^:::endif\s*$/gm;

  // Loop to handle nested conditional blocks — each pass resolves the
  // innermost blocks first, which then exposes outer blocks for the next pass.
  let processed = content;
  let prev;
  do {
    prev = processed;
    processed = processed.replace(pattern, (match, condition, body) => {
      if (evaluateCondition(condition, ctx)) {
        return body;
      }
      return '';
    });
  } while (processed !== prev);

  // Normalize excessive blank lines left by removed blocks
  return processed.replace(/\n{3,}/g, '\n\n');
}
